/** @module What the user decided per line of an imported list, and the lines to book from it. */
import type { components } from "@/api/client";

export type ImportRow = components["schemas"]["ImportRowOut"];
export type Candidate = components["schemas"]["ImportCandidateOut"];
export type ImportLine = components["schemas"]["ImportLineIn"];
type Card = components["schemas"]["CardPrintOut"];

export interface Choice {
  include: boolean;
  candidate: Candidate | null;
  variantId: number | null;
}

/** Choices by line number of the file. */
export type Choices = Record<number, Choice>;

const NONE: Choice = { include: false, candidate: null, variantId: null };

/** Recognised lines are in, everything else waits for a decision. */
export function initialChoices(rows: ImportRow[]): Choices {
  const choices: Choices = {};
  for (const row of rows) {
    const first = row.candidates[0];
    choices[row.line] =
      row.status === "matched" && first
        ? { include: true, candidate: first, variantId: first.variant_id }
        : NONE;
  }
  return choices;
}

/** Taking a card means booking it, with the variant the server derived from the line. */
export function choose(candidate: Candidate): Choice {
  return {
    include: true,
    candidate,
    variantId: candidate.variant_id ?? candidate.variants[0]?.id ?? null,
  };
}

/** A card found by hand, in the shape of a server suggestion. */
export function candidateOf(card: Card): Candidate {
  const variants = [...card.variants]
    .sort((a, b) => a.rank - b.rank || a.variant_key.localeCompare(b.variant_key))
    .map((variant) => ({ id: variant.id, variant_key: variant.variant_key }));
  return {
    card: {
      print_id: card.id,
      name: card.name,
      name_lang: card.name_lang,
      set_id: card.set_id,
      set_code: card.set_code ?? null,
      set_total: card.set_total ?? null,
      local_id: card.local_id,
      rarity: card.rarity,
      rarity_class: card.rarity_class,
      variant_key: variants[0]?.variant_key ?? "normal",
    },
    variant_id: variants[0]?.id ?? null,
    variants,
  };
}

export function choiceOf(choices: Choices, row: ImportRow): Choice {
  return choices[row.line] ?? NONE;
}

export function isReady(choice: Choice): boolean {
  return choice.include && choice.variantId !== null;
}

/** Lines that still need a look: not recognised and not decided yet. */
export function isOpen(row: ImportRow, choice: Choice): boolean {
  return row.status !== "matched" && !isReady(choice) && choice.candidate === null;
}

/**
 * The lines to book. The list's own source wins; the one typed for the whole file only
 * fills lines that have a price, because a source without a purchase means nothing.
 */
export function commitLines(rows: ImportRow[], choices: Choices, source: string): ImportLine[] {
  const fallback = source.trim() || null;
  return rows.flatMap((row) => {
    const choice = choiceOf(choices, row);
    if (!isReady(choice) || choice.variantId === null) return [];
    const priced = row.purchase_price !== null;
    return [
      {
        variant_id: choice.variantId,
        language: row.language,
        condition: row.condition as ImportLine["condition"],
        quantity: row.quantity,
        purchase_price: row.purchase_price,
        purchased_on: priced ? row.purchased_on : null,
        purchase_source: priced ? (row.purchase_source ?? fallback) : null,
        note: row.note,
      },
    ];
  });
}

export function summary(rows: ImportRow[], choices: Choices) {
  let lines = 0;
  let copies = 0;
  let open = 0;
  for (const row of rows) {
    const choice = choiceOf(choices, row);
    if (isReady(choice)) {
      lines += 1;
      copies += row.quantity;
    } else if (isOpen(row, choice)) {
      open += 1;
    }
  }
  return { lines, copies, open };
}
